import React, { useEffect, useState, FC, FormEvent } from 'react';
import { Button } from '@goldbely/explore-component-library';
import cls from 'classnames';
import { useForm, FieldValues } from 'react-hook-form';

import api, { DEFAULT_ERROR } from 'src/explore/services/api';
import { Spinner } from 'src/shoppe/elements/Spinner';

import Errors from './molecules/Errors';
import Footer from './molecules/Footer';
import Header from './molecules/Header';
import Inputs from './molecules/Inputs';

import styles from './index.module.sass';

interface FormProps {
  discountLabel: string;
  step: number;
  onDecline: () => void;
  onStep: () => void;
}

const Form: FC<FormProps> = ({ discountLabel, step, onDecline, onStep }) => {
  const [ apiError, setApiError ] = useState( '' );
  const [ isLoading, setIsLoading ] = useState( false );

  const {
    formState: { errors: emailErrors, isDirty: isDirtyEmail, isValid: isValidEmail },
    getValues: getEmailValues,
    register: registerEmail,
    trigger: triggerEmail,
    watch: watchEmail,
  } = useForm<FieldValues>();
  const { 'first-order-email': email } = watchEmail();

  const {
    control: controlPhone,
    formState: { errors: phoneErrors, isDirty: isDirtyPhone, isValid: isValidPhone },
    getValues: getPhoneValues,
    register: registerPhone,
    setFocus: setFocusPhone,
    trigger: triggerPhone,
    watch: watchPhone,
  } = useForm<FieldValues>();
  const { 'first-order-phone': phone } = watchPhone();

  const submitEmail = async () => {
    const showEligibilityError = () => {
      setApiError( 'This email is not eligible for a discount.' );
      setIsLoading( false );
    };
    const showValidationError = () => {
      setApiError( 'This email is not valid.' );
      setIsLoading( false );
    };

    const trackEmail = () => {
      api
        .post( 'promotions/first_time_customer_incentive/submit_email', {
          email: getEmailValues( 'first-order-email' ),
        })
        .then(({ status }) => {
          if ( status === 204 ) {
            onStep();
            window.requestAnimationFrame(() => {
              setFocusPhone( 'first-order-phone' );
            });
            setIsLoading( false );
          } else {
            showValidationError();
          }
        })
        .catch(() => {
          showValidationError();
        });
    };

    triggerEmail();
    if ( isValidEmail ) {
      setIsLoading( true );
      setApiError( '' );

      api
        .get( '/account_status', {
          params: {
            email: getEmailValues( 'first-order-email' ),
          },
        })
        .then(({ data }) => {
          if ( data.fod_eligible === true ) {
            trackEmail();
          } else {
            showEligibilityError();
          }
        })
        .catch(({ response: { status }}) => {
          if ( status === 404 ) {
            trackEmail();
          } else {
            showEligibilityError();
          }
        });
    }
  };

  const createDiscount = async () => {
    const showDiscountError = ( error?: string ) => {
      setApiError( error || DEFAULT_ERROR );
      setIsLoading( false );
    };

    triggerPhone();
    if ( isValidPhone ) {
      setIsLoading( true );
      setApiError( '' );

      api
        .post( 'promotions/first_time_customer_incentive', {
          email: getEmailValues( 'first-order-email' ),
          phone_number: getPhoneValues( 'first-order-phone' ),
        })
        .then(({ data: { error }}) => {
          if ( error ) {
            showDiscountError( error.message );
          } else {
            onStep();
            setIsLoading( false );
          }
        })
        .catch(( error ) => {
          showDiscountError( error.response?.data?.message );
        });
    }
  };

  const handleSubmit = ( event: FormEvent ) => {
    event.preventDefault();

    if ( step === 1 ) {
      submitEmail();
    }
    if ( step === 2 ) {
      createDiscount();
    }
  };

  useEffect(() => {
    setApiError( '' );
  }, [ email, phone ]);

  if ( step > 2 ) return null;

  return (
    <>
      <Header discountLabel={discountLabel} />

      <form className={cls( styles.container, styles[`step-${step}`])} onSubmit={handleSubmit}>
        <Inputs
          email={{
            errors: emailErrors,
            register: registerEmail,
          }}
          isLoading={isLoading}
          phone={{
            control: controlPhone,
            errors: phoneErrors,
            register: registerPhone,
          }}
          step={step}
        />

        <Errors api={apiError} email={emailErrors} phone={phoneErrors} />

        <Button
          className={`${styles.submit} spec__submit-first-order-promotion`}
          disabled={
            !!apiError ||
            ( step === 1 && ( !isDirtyEmail || !isValidEmail )) ||
            ( step === 2 && ( !isDirtyPhone || !isValidPhone ))
          }
          fullWidth
          icon={isLoading && <Spinner className={`${styles.spinner} spinner-border`} />}
          loading={isLoading}
          type="submit"
        >
          {step === 1 ? 'Continue' : `Claim Your ${discountLabel} Off`}
        </Button>
      </form>

      <Footer step={step} onDecline={onDecline} />
    </>
  );
};

export default Form;
